import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

function buildPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, '…', total];
  }

  if (current >= total - 3) {
    return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, '…', current - 1, current, current + 1, '…', total];
}

/**
 * Reusable pagination with numbered pages, ellipsis and range info.
 * Keeps working during search because the parent sends limit/skip to the API.
 */
export default function Pagination({
  page,
  totalPages,
  totalItems,
  limit,
  onChange,
  loading = false,
  itemLabel = 'produk',
}) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);

  const firstItem = totalItems === 0 ? 0 : (safePage - 1) * limit + 1;
  const lastItem = Math.min(safePage * limit, totalItems);
  const items = buildPageItems(safePage, safeTotalPages);

  const goTo = (nextPage) => {
    if (loading) return;
    if (nextPage < 1 || nextPage > safeTotalPages || nextPage === safePage) return;
    onChange(nextPage);
  };

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
      <p className="order-2 text-sm text-slate-500 sm:order-1">
        Menampilkan{' '}
        <span className="font-semibold text-slate-800">
          {firstItem}–{lastItem}
        </span>{' '}
        dari <span className="font-semibold text-slate-800">{totalItems}</span> {itemLabel}
      </p>

      <div className="order-1 max-w-full overflow-x-auto no-scrollbar sm:order-2">
        <nav
          aria-label="Pagination"
          className="mx-auto flex w-max items-center gap-1.5 sm:w-auto"
        >
        <button
          type="button"
          onClick={() => goTo(safePage - 1)}
          disabled={safePage === 1 || loading}
          aria-label="Halaman sebelumnya"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-600"
        >
          <ChevronLeftIcon className="h-4 w-4" strokeWidth={2.5} />
        </button>

        <ul className="flex items-center gap-1.5">
          {items.map((item, index) =>
            item === '…' ? (
              <li
                key={`ellipsis-${index}`}
                className="flex h-10 w-8 items-center justify-center text-sm text-slate-400"
                aria-hidden="true"
              >
                …
              </li>
            ) : (
              <li key={item}>
                <button
                  type="button"
                  onClick={() => goTo(item)}
                  disabled={loading}
                  aria-current={item === safePage ? 'page' : undefined}
                  aria-label={`Halaman ${item}`}
                  className={`flex h-10 min-w-[2.5rem] items-center justify-center rounded-xl px-2 text-sm font-semibold transition ${
                    item === safePage
                      ? 'bg-brand-600 text-white shadow-lift'
                      : 'border border-transparent text-slate-600 hover:border-slate-200 hover:bg-white hover:text-brand-700'
                  } disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {item}
                </button>
              </li>
            )
          )}
        </ul>

        <button
          type="button"
          onClick={() => goTo(safePage + 1)}
          disabled={safePage === safeTotalPages || loading}
          aria-label="Halaman berikutnya"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-600"
        >
          <ChevronRightIcon className="h-4 w-4" strokeWidth={2.5} />
        </button>
        </nav>
      </div>
    </div>
  );
}
